"use client";

/**
 * Navigation for a screen nobody is holding.
 *
 * The bottom tab bar exists because of the thumb. At a metre's distance with a
 * pointer, that argument disappears entirely and the opposite one applies: the
 * bottom edge of a 1080-point-tall window is the furthest thing from where the
 * eye starts, and horizontal space is what there is too much of. So past
 * `WIDE` the same five destinations become a rail on the left, the wordmark
 * comes with them, and the labels sit beside the icons rather than under
 * them — a 64px column of stacked icon-and-caption is a phone tab bar rotated,
 * which is the mistake this is avoiding.
 *
 * Same five destinations, same order. What changes is the geometry, not the
 * model: somebody moving between their phone and a laptop should not have to
 * relearn where anything is.
 *
 * THE LAYOUT IS A CREATIVE TOOL'S, NOT AN ADMIN'S. Wordmark, then who you are
 * working as, then the places to go, then the way out to settings and the
 * account at the foot. 240 wide with 15px labels on 32px rows and a 36px
 * pitch: a creator visits a few times a week and reads the rail as a menu,
 * not as a dense tool's index.
 *
 * It folds to its icons from the toggle beside the wordmark, and back out from
 * the "One" that is all that is left of the wordmark when folded. The
 * wordmark's own last word is the way back, so the control that opens the
 * rail sits exactly where the one that closed it was pointing.
 *
 * Every row is the same row. Submit used to be filled brand, which made the
 * rail's loudest object a place you go once per video. The phone's tab bar
 * keeps its filled action; this is the desktop rail only.
 */

import {
  ClipboardList,
  Home,
  MessageCircle,
  Plus,
  Settings,
  Sparkles,
  Video,
} from "lucide-react";
import type { ReactNode } from "react";
import { SidebarToggleIcon } from "@/components/ui/design-system/icons";
import { Skeleton } from "@/components/ui/design-system/skeleton";
import { Rail } from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { RAIL_MOTION, ROW_PRESS } from "./chrome";
import type { Tab } from "./tabs";
import { NAV_LABELS } from "./tabs";

const ROW =
  "flex h-[32px] w-full items-center gap-nf-ds-2 rounded-nf-ds-md px-nf-ds-3 text-left outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-brand";

// No `truncate`: mid-fold an ellipsis flickers through every label. The labels
// are short and the rail clips them, so nowrap is all a row needs.
//
// Trimmed to the cap height so `items-center` centres the letters, not the
// line box. Untrimmed, the box carries the font's descender space below the
// text, so the glyphs sit visibly off the icon's centre line.
//
// 14px, the DS small size, not 15: a rail is navigation beside the page, and
// at the page's own reading size its labels competed with the content they
// lead to. Weight stays 500, the label weight.
const LABEL =
  "min-w-0 whitespace-nowrap font-nf-ds-sans text-nf-ds-sm font-[500] leading-none [text-box:trim-both_cap_alphabetic]";

/**
 * What fades with the fold. Out quickly, before the column has narrowed
 * enough to crop it; back in after a beat, once there is room for it to
 * arrive whole. Symmetric timing would show half a word both ways.
 */
const FADE =
  "transition-opacity ease-out motion-reduce:transition-none motion-reduce:delay-0";
const fade = (hidden: boolean) =>
  cn(
    FADE,
    hidden ? "opacity-0 duration-100" : "opacity-100 duration-150 delay-75",
  );

/**
 * Crops what overhangs a section mid-fold, with room for the 2px focus ring
 * the rows draw outside their own box. `clip` rather than `hidden`, so it
 * makes no scroll container.
 */
const FOLD_CLIP = "overflow-clip [overflow-clip-margin:4px]";

const ITEMS: { id: Tab; icon: typeof Home }[] = [
  { id: "home", icon: Home },
  { id: "requests", icon: ClipboardList },
  { id: "videos", icon: Video },
  { id: "submit", icon: Plus },
  { id: "inbox", icon: MessageCircle },
  { id: "updates", icon: Sparkles },
];

export function SideRail({
  tab,
  onTab,
  unread,
  unseen,
  switcher,
  email,
  onSettings,
  collapsed,
  onCollapsedChange,
}: {
  tab: Tab;
  onTab: (next: Tab) => void;
  unread: number;
  unseen: number;
  /**
   * The program control, supplied by the host rather than built here.
   *
   * The prototype hands in its fixture `Switcher` and the portal hands in the
   * real `ProgramSwitcher`, which knows about access and tRPC. A slot is what
   * lets one rail serve both without the product growing a fixture or the
   * prototype growing a data layer.
   */
  switcher?: ReactNode;
  /** The account this is being read as. */
  email: string;
  /** Opens the settings sheet, which is where sign-out lives. */
  onSettings: () => void;
  /** Folded to icons. The host owns it, because the content offset follows. */
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
}) {
  const t = useCreatorTranslations("shell");
  const badge: Partial<Record<Tab, boolean>> = {
    inbox: unread > 0,
    updates: unseen > 0,
  };

  // Home is the overview, so the whole menu is lit there: nothing has been
  // chosen yet. Anywhere else the rail steps back and only the current row
  // stays bright, which is what makes "you are here" legible at a glance.
  const lit = (id: Tab) => tab === "home" || tab === id;
  const tone = (on: boolean) =>
    on ? "text-nf-ds-fg" : "text-muted-foreground";
  // Folded, a label has no room and the row is a glyph. It stays in the tree
  // at zero opacity, so a screen reader still hears the destination's name
  // and the native tooltip says it for everyone else.
  const label = cn(LABEL, fade(collapsed));

  return (
    <nav
      aria-label={t("nav.label")}
      className={cn(
        // `RAIL_W` and `RAIL_W_COLLAPSED` in pixels; the content column's
        // offset reads the same numbers and moves on the same curve.
        // No overflow clip here. The program list opens past the rail's
        // edge, so the sections that fold clip themselves (`FOLD_CLIP`).
        "flex shrink-0 flex-col gap-nf-ds-4 p-nf-ds-2 pt-nf-ds-4",
        "transition-[width]",
        RAIL_MOTION,
        collapsed ? "w-[56px]" : "w-[240px]",
        // A LINE, DELIBERATELY, against the surface's own "no dividers" rule.
        //
        // That rule is about panes: two cards on a page separate by fill and
        // light, and a drawn line between them is what makes soft shapes read
        // as boxes. This is not two panes. It is the edge of the application
        // frame — the boundary between the chrome you navigate with and the
        // document you are reading — and space cannot draw it, because the
        // rail is pinned to the viewport edge and the content scrolls under
        // nothing.
        //
        // `app-frame-line`, NOT the general Line role. `--nf-ds-border` is
        // deliberately `transparent` in the candidate layer, so drawing this
        // edge with that token means drawing nothing.
        "border-nf-ds-app-frame-line border-r",
      )}
    >
      {/*
        Both headers are always here, stacked, and cross-fade. Swapping one
        for the other is a cut in the middle of a slide; this way the
        wordmark dissolves into the "One" that stays.
      */}
      <div className={cn("relative h-[32px] shrink-0", FOLD_CLIP)}>
        <div
          inert={collapsed}
          className={cn(
            "absolute inset-y-0 left-0 flex w-[224px] items-center justify-between gap-nf-ds-2 pl-nf-ds-3",
            fade(collapsed),
          )}
        >
          <span className="flex min-w-0 items-baseline gap-nf-ds-2 whitespace-nowrap">
            <span className="font-swear text-[19px] leading-none tracking-[-0.01em]">
              NewForm
            </span>
            <Rail className="text-nf-ds-brand">One</Rail>
          </span>
          <button
            type="button"
            onClick={() => onCollapsedChange(true)}
            aria-label={t("rail.collapse")}
            title={t("rail.collapse")}
            className={cn(
              "grid size-[32px] shrink-0 place-items-center rounded-nf-ds-md text-muted-foreground outline-hidden",
              "hover:bg-nf-ds-fg/[0.04] hover:text-nf-ds-fg focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
              ROW_PRESS,
            )}
          >
            <SidebarToggleIcon size={16} />
          </button>
        </div>
        <button
          type="button"
          inert={!collapsed}
          onClick={() => onCollapsedChange(false)}
          aria-label={t("rail.expand")}
          title={t("rail.expand")}
          className={cn(
            ROW,
            ROW_PRESS,
            "absolute inset-y-0 left-0 w-[40px] justify-center px-0 hover:bg-nf-ds-fg/[0.04]",
            fade(!collapsed),
          )}
        >
          <Rail className="text-nf-ds-brand">One</Rail>
        </button>
      </div>

      {/* Who you are working as, before where you can go: switching program
          changes what every row below it shows. Folded, it keeps the
          program's tile, which still opens the list. Not clipped like the
          sections around it: the list is wider than the rail and has to
          reach past its edge. */}
      {switcher}

      <div className={cn("flex flex-col gap-nf-ds-1", FOLD_CLIP)}>
        {ITEMS.map((item) => {
          const active = tab === item.id;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              aria-current={active ? "page" : undefined}
              onClick={() => onTab(item.id)}
              title={collapsed ? t(NAV_LABELS[item.id]) : undefined}
              className={cn(
                ROW,
                ROW_PRESS,
                tone(lit(item.id)),
                active
                  ? // A lift off the rail, not a well in it: the ground role
                    // is darker than the rail in the dark theme and vanished.
                    // Foreground-alpha flips with the theme, and the ring is
                    // the card edge the DS already uses.
                    "bg-nf-ds-fg/[0.08] ring-1 ring-nf-ds-surface-card-ring ring-inset"
                  : "hover:bg-nf-ds-fg/[0.04]",
              )}
            >
              <span className="relative grid size-[16px] shrink-0 place-items-center">
                <Icon className="size-[16px]" strokeWidth={1.75} />
                {badge[item.id] ? (
                  <span
                    aria-hidden="true"
                    className="absolute -top-[3px] -right-[4px] size-[7px] rounded-full bg-nf-ds-brand ring-2 ring-nf-ds-surface-card"
                  />
                ) : null}
              </span>
              <span className={label}>{t(NAV_LABELS[item.id])}</span>
              {badge[item.id] ? (
                <span className="sr-only">
                  {item.id === "inbox"
                    ? t("nav.unread", { count: unread })
                    : t("nav.unreadUpdates")}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/*
        The foot: a way into settings, and the account it applies to.

        The email is a caption, not a control. It used to share a row with
        the settings glyph, which made the address look pressable and the
        destination look like decoration. Now the row says what it opens and
        the line under it says who you are.
      */}
      <div
        className={cn(
          "mt-auto flex flex-col gap-nf-ds-2 pb-nf-ds-2",
          FOLD_CLIP,
        )}
      >
        <button
          type="button"
          onClick={onSettings}
          title={collapsed ? t("nav.settings") : undefined}
          className={cn(
            ROW,
            ROW_PRESS,
            tone(tab === "home"),
            "hover:bg-nf-ds-fg/[0.04]",
          )}
        >
          <Settings
            aria-hidden="true"
            className="size-[16px] shrink-0"
            strokeWidth={1.75}
          />
          <span className={label}>{t("nav.settings")}</span>
        </button>

        {/* The address arrives with the session, a beat after the rail
            paints. A line of the right height keeps the foot from growing
            into place. */}
        <div
          aria-hidden={collapsed}
          className={cn(
            "flex h-[16px] w-[224px] items-center px-nf-ds-3",
            fade(collapsed),
          )}
        >
          {email ? (
            <span
              className="min-w-0 truncate font-nf-ds-sans text-[12px] text-muted-foreground"
              title={email}
            >
              {email}
            </span>
          ) : (
            <Skeleton className="h-[12px] w-full" />
          )}
        </div>
      </div>
    </nav>
  );
}
